import type { ApplicationRegister } from '/@/types/application-register';
import type { ApplicationReport } from '/@/types/application-report';
import type { CropMonitoring } from '/@/types/crop-monitoring';
import type { Recommendation } from '/@/types/recommendation';

/**
 * Crop-protection recommendations (weed/disease/plague/seed-treatment) are windowed and
 * placed by their created date; seed & density recommendations (corn/soy/corn-density) by
 * their planting date.
 */
export const isCropProtectionRecommendation = (recommendation: Recommendation): boolean =>
    Boolean(
        recommendation.weedV2 ||
            recommendation.diseaseRecommendation ||
            recommendation.plagueRecommendation ||
            recommendation.seedTreatmentRecommendation
    );

/**
 * How a recommendation is registered as "applied":
 * - `corn` / `soy` / `corn_density` → New Crop Monitoring modal
 * - `crop_protection` → manual application modal
 * - `other` → cannot be registered
 */
export type RegistrationKind = 'corn' | 'soy' | 'corn_density' | 'crop_protection' | 'other';

/**
 * The date that bounds a recommendation to the campaign and places it on the timeline —
 * also shown as its "Fecha" so a card's visible dates always explain why it landed there.
 */
export const recommendationDate = (recommendation: Recommendation): string =>
    isCropProtectionRecommendation(recommendation)
        ? recommendation.created
        : (recommendation.cornDensity?.plantingDate ??
          recommendation.crops?.[0]?.plantingDate ??
          recommendation.created);

export const getRegistrationKind = (recommendation: Recommendation): RegistrationKind => {
    if (isCropProtectionRecommendation(recommendation)) return 'crop_protection';
    if (recommendation.cornDensity) return 'corn_density';
    const crop = recommendation.crops?.[0]?.crop;
    if (crop === 'corn') return 'corn';
    if (crop === 'soy') return 'soy';
    return 'other';
};

/** The reco field holding the crop-protection result, or undefined for non-CP recos. */
const cropProtectionResult = (recommendation: Recommendation): { options?: unknown[] } | undefined => {
    const reco = recommendation as unknown as Record<string, { result?: { options?: unknown[] } } | null>;
    return (
        reco.weedV2?.result ??
        reco.diseaseRecommendation?.result ??
        reco.plagueRecommendation?.result ??
        reco.seedTreatmentRecommendation?.result ??
        undefined
    );
};

/**
 * Product names recommended by a crop-protection reco, collected across all of its options
 * (deduped). Options are alternative/conditional programs and any of them may be empty — so we
 * can't assume the products live in the first one.
 */
export const getCropProtectionProductNames = (recommendation: Recommendation): string[] => {
    const result = cropProtectionResult(recommendation);
    const options = (result?.options ?? []) as {
        columns?: { products?: { product?: { name?: string } }[] }[];
    }[];
    const names = options
        .flatMap((option) => option.columns ?? [])
        .flatMap((column) => column.products ?? [])
        .map((productConfig) => productConfig.product?.name)
        .filter((name): name is string => Boolean(name));
    return [...new Set(names)];
};

/** Case/whitespace-insensitive product-name key for matching across data sources. */
export const normalizeProductName = (name: string): string => name.trim().toLowerCase();

const normalize = normalizeProductName;

/**
 * Product names a seed/density recommendation recommends: the hybrid product names for
 * corn/soy recos, the product-profile name for corn-density recos.
 */
export const getSeedRecommendationProductNames = (recommendation: Recommendation): string[] => {
    if (recommendation.cornDensity) {
        return [recommendation.cornDensity.productProfileName].filter((name): name is string => Boolean(name));
    }
    const cropReco = recommendation.crops?.[0];
    if (!cropReco) return [];
    return [...(cropReco.cornProductsNames ?? []), ...(cropReco.soyProductsNames ?? [])].filter(Boolean);
};

/** The product names a recommendation recommends, regardless of its type. */
export const getRecommendedProductNames = (recommendation: Recommendation): string[] =>
    isCropProtectionRecommendation(recommendation)
        ? getCropProtectionProductNames(recommendation)
        : getSeedRecommendationProductNames(recommendation);

/** Normalized set of every product name recommended across the given recommendations. */
export const buildRecommendedNameSet = (recommendations: Recommendation[]): Set<string> => {
    const set = new Set<string>();
    for (const recommendation of recommendations) {
        for (const name of getRecommendedProductNames(recommendation)) set.add(normalize(name));
    }
    return set;
};

/**
 * Names of crop-protection products already registered on the field, from manual application
 * registers and FieldView application reports (matched by name — FieldView reports carry no
 * catalog id).
 */
export const getRegisteredProductNames = (
    applications: ApplicationRegister[],
    applicationReports: ApplicationReport[]
): Set<string> => {
    const names = new Set<string>();
    for (const application of applications) {
        if (application.cropProtectionProductName) names.add(normalize(application.cropProtectionProductName));
    }
    for (const report of applicationReports) {
        for (const result of report.results) {
            if (result.chemicalProductName) names.add(normalize(result.chemicalProductName));
        }
    }
    return names;
};

/** A crop-protection reco is "already registered" when every product it recommends is registered. */
export const isCropProtectionAlreadyRegistered = (
    recommendation: Recommendation,
    registeredNames: Set<string>
): boolean => {
    const productNames = getCropProtectionProductNames(recommendation);
    if (productNames.length === 0) return false;
    return productNames.every((name) => registeredNames.has(normalize(name)));
};

/** A crop monitoring blocks seed/density registration unless it has already been harvested. */
export const hasActiveCropMonitoring = (cropMonitoring?: CropMonitoring): boolean =>
    Boolean(cropMonitoring?.id) && !cropMonitoring?.harvestDate;

/**
 * Whether a recommendation's "Registrar lo aplicado" action is available. See {@link RegistrationKind}.
 */
export const canRegisterRecommendation = (
    recommendation: Recommendation,
    { cropMonitoring, registeredNames }: { cropMonitoring?: CropMonitoring; registeredNames: Set<string> }
): boolean => {
    switch (getRegistrationKind(recommendation)) {
        case 'crop_protection':
            return !isCropProtectionAlreadyRegistered(recommendation, registeredNames);
        case 'corn':
        case 'soy':
        case 'corn_density':
            return !hasActiveCropMonitoring(cropMonitoring);
        default:
            return false;
    }
};
